import { format } from 'date-fns';

import type { TransactionType } from '@/api/fund/types';
import {
  type TransactionApiItem,
  TransactionFeePayer,
  type TransactionViewCounterparty,
  TransactionViewCounterpartyType,
  TransactionViewDirection,
  type TransactionViewFee,
  type TransactionViewLine,
} from '@/api/transaction-views/queries';
import { getNormalizedSuiAddress } from '@/shared/modules/wallet/send/recipient';

import { formatFee, isGasRebate, signFeeValue } from './transaction-detail';

export interface TransactionListCoin {
  symbol: string;
  iconUrl: string | null;
}

export interface TransactionListLine {
  amount: string | null;
  amountInUsd: string | null;
  denomination: string | null;
  coin: TransactionListCoin | null;
  counterpartyLabel: string;
  counterpartyAddress: string | null;
  counterpartyDisplayName: string | null;
}

export interface TransactionListGas {
  label: string;
  amountLabel: string;
  payerLabel: string;
  coin: TransactionListCoin | null;
  amountInUsd: string | null;
}

export interface TransactionListItem {
  id: string;
  date: string;
  direction: TransactionViewDirection | null;
  type: TransactionType;
  lines: TransactionListLine[];
  gas: TransactionListGas | null;
  transactionHash: string | null;
  notes: string | null;
}

export type TransactionSortField = 'direction' | 'fund' | 'date' | 'amount' | 'type' | 'notes';

export type TransactionSortDirection = 'asc' | 'desc';

export interface TransactionListFilters {
  searchTerm: string;
  dateRange: readonly [Date | null, Date | null];
  direction: string;
  type: TransactionType | '';
  denomination: string;
  sortField: TransactionSortField;
  sortDirection: TransactionSortDirection;
}

const DIRECTION_LABELS: Record<TransactionViewDirection, string> = {
  [TransactionViewDirection.Incoming]: 'Incoming',
  [TransactionViewDirection.Outgoing]: 'Outgoing',
  [TransactionViewDirection.TwoWay]: 'Two-way',
};

export const mapTransactionItems = (
  items: readonly TransactionApiItem[],
): TransactionListItem[] => {
  const seen = new Set<string>();

  return items.flatMap((transaction) => {
    if (seen.has(transaction.id)) {
      return [];
    }
    seen.add(transaction.id);
    return [toTransactionListItem(transaction)];
  });
};

export const getCounterpartyWalletAddress = (
  counterparty: TransactionViewCounterparty | null,
): string | null =>
  counterparty?.type === TransactionViewCounterpartyType.Wallet
    ? getNormalizedSuiAddress(counterparty.id)
    : null;

export const getTransactionWalletAddresses = (
  transactions: readonly TransactionListItem[],
): string[] => [
  ...new Set(
    transactions.flatMap((transaction) =>
      transaction.lines.flatMap((line) =>
        line.counterpartyAddress ? [line.counterpartyAddress] : [],
      ),
    ),
  ),
];

export const applyCounterpartyDisplayNames = (
  transactions: readonly TransactionListItem[],
  displayNames: ReadonlyMap<string, string>,
): TransactionListItem[] =>
  transactions.map((transaction) => ({
    ...transaction,
    lines: transaction.lines.map((line) => withCounterpartyDisplayName(line, displayNames)),
  }));

export const formatTransactionDirection = (direction: TransactionViewDirection | null): string =>
  direction ? DIRECTION_LABELS[direction] : '—';

export const formatTransactionUsdValue = (value: string | null): string | null => {
  if (value === null || value.trim() === '') {
    return null;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(numericValue));
};

export const formatTransactionDate = (value: string): string => {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? format(new Date(timestamp), 'yyyy-MM-dd HH:mm') : '—';
};

const toTransactionListItem = (transaction: TransactionApiItem): TransactionListItem => {
  const isOnchain = transaction.onchain !== null;
  return {
    id: transaction.id,
    date: transaction.date,
    direction: transaction.direction,
    type: transaction.type,
    lines: transaction.lines.map((line) => toTransactionListLine(line, isOnchain)),
    gas: toTransactionListGas(transaction.fee),
    transactionHash: transaction.onchain?.hash ?? null,
    notes: transaction.notes,
  };
};

const toTransactionListLine = (
  line: TransactionViewLine,
  isOnchain: boolean,
): TransactionListLine => ({
  amount: line.amount,
  amountInUsd: line.amountInUsd,
  denomination: line.denomination,
  coin: isOnchain ? toTransactionListCoin(line) : null,
  counterpartyLabel: getCounterpartyLabel(line.counterparty),
  counterpartyAddress: getCounterpartyWalletAddress(line.counterparty),
  counterpartyDisplayName: null,
});

const toTransactionListGas = (fee: TransactionViewFee | null): TransactionListGas | null => {
  if (!fee || fee.payer === TransactionFeePayer.Sender) {
    return null;
  }
  const rebate = isGasRebate(fee);
  return {
    label: `${rebate ? 'gas rebate' : 'gas'} ${formatFee(fee)}`,
    amountLabel: `${rebate ? 'Rebate' : 'Fee'} ${formatCompactFeeAmount(fee)}`,
    payerLabel: formatFee(fee).split(' · ')[1] ?? '',
    coin: toTransactionListCoin(fee),
    amountInUsd: fee.amountInUsd === null ? null : signFeeValue(fee.amountInUsd, fee.payer),
  };
};

const formatCompactFeeAmount = (fee: TransactionViewFee): string => {
  if (fee.amount === null) return '—';
  const amount = Number(fee.amount);
  if (!Number.isFinite(amount)) return '—';
  const value = new Intl.NumberFormat('en-US', {
    maximumSignificantDigits: 3,
    useGrouping: false,
  }).format(Math.abs(amount));
  if (fee.payer !== TransactionFeePayer.Wallet) return value;
  return `${isGasRebate(fee) ? '+' : '−'}${value}`;
};

const toTransactionListCoin = (
  asset: Pick<TransactionViewLine, 'denomination' | 'iconUrl'>,
): TransactionListCoin | null =>
  asset.denomination ? { symbol: asset.denomination, iconUrl: asset.iconUrl } : null;

const getCounterpartyLabel = (counterparty: TransactionViewCounterparty | null): string => {
  if (counterparty?.type === TransactionViewCounterpartyType.Wallet) {
    return counterparty.id;
  }
  return counterparty?.name || counterparty?.id || '—';
};

const withCounterpartyDisplayName = (
  line: TransactionListLine,
  displayNames: ReadonlyMap<string, string>,
): TransactionListLine => {
  const displayName = line.counterpartyAddress
    ? displayNames.get(line.counterpartyAddress)
    : undefined;
  return displayName ? { ...line, counterpartyDisplayName: displayName } : line;
};
